import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function ChroniclesStudioOnboarding() {
  const navigate = useNavigate();
  const [studioName, setStudioName] = useState('Carlton');
  const [primaryFocus, setPrimaryFocus] = useState('both');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const userEmail = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').email || ''; }
    catch { return ''; }
  })();

  const handleSubmit = async () => {
    if (!studioName.trim()) { setError('Please enter a studio name.'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/v1/chronicles-studio/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_email: userEmail,
          studio_name: studioName.trim(),
          primary_focus: primaryFocus,
        }),
      });
      if (!res.ok) throw new Error('Setup failed');
      navigate('/mindstreams-studio');
    } catch (err) {
      setError('Setup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const focusOptions = [
    { value: 'develop', label: '✍️ Develop Episodes', desc: 'Write, analyze, and build episodes' },
    { value: 'explore', label: '📖 Explore Lore', desc: 'Query the universe, characters, and world-building' },
    { value: 'both', label: '🎬 Both', desc: 'Full studio access' },
  ];

  return (
    <div style={styles.bg}>
      <div style={styles.container}>
        {/* Logo / Header */}
        <div style={styles.logoArea}>
          <div style={styles.logoIcon}>🎬</div>
          <h1 style={styles.title}>MindStreams Studio</h1>
          <p style={styles.subtitle}>The FHO Chronicles Creative Platform</p>
          <div style={styles.divider} />
        </div>

        {/* Form */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Welcome to the Studio</h2>
          <p style={styles.cardDesc}>Let's get you set up. This only takes a moment.</p>

          {/* Studio Name */}
          <div style={styles.fieldGroup}>
            <label style={styles.label}>How should the Studio address you?</label>
            <input
              style={styles.input}
              value={studioName}
              onChange={e => setStudioName(e.target.value)}
              placeholder="Carlton"
              maxLength={40}
            />
          </div>

          {/* Primary Focus */}
          <div style={styles.fieldGroup}>
            <label style={styles.label}>What will you primarily use the Studio for?</label>
            <div style={styles.focusGrid}>
              {focusOptions.map(opt => (
                <button
                  key={opt.value}
                  style={{
                    ...styles.focusBtn,
                    ...(primaryFocus === opt.value ? styles.focusBtnActive : {})
                  }}
                  onClick={() => setPrimaryFocus(opt.value)}
                >
                  <span style={styles.focusBtnLabel}>{opt.label}</span>
                  <span style={styles.focusBtnDesc}>{opt.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {error && <p style={styles.errorMsg}>{error}</p>}

          <button
            style={{ ...styles.submitBtn, opacity: loading ? 0.7 : 1 }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Setting up...' : 'Enter the Studio →'}
          </button>
        </div>

        <p style={styles.footer}>MindStreams Technology LLC · Carlton E. Thornton Jr.</p>
      </div>
    </div>
  );
}

const styles = {
  bg: {
    minHeight: '100vh',
    background: '#0a0f1e',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 16px',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  container: {
    width: '100%',
    maxWidth: '540px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '32px',
  },
  logoArea: {
    textAlign: 'center',
  },
  logoIcon: {
    fontSize: '52px',
    marginBottom: '12px',
    display: 'block',
  },
  title: {
    margin: 0,
    fontSize: '2rem',
    fontWeight: '700',
    color: '#d4af37',
    letterSpacing: '0.04em',
  },
  subtitle: {
    margin: '8px 0 20px',
    fontSize: '0.9rem',
    color: 'rgba(255,255,255,0.5)',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  divider: {
    width: '80px',
    height: '2px',
    background: 'linear-gradient(90deg, transparent, #d4af37, transparent)',
    margin: '0 auto',
  },
  card: {
    width: '100%',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(212,175,55,0.2)',
    borderRadius: '16px',
    padding: '36px 32px',
    backdropFilter: 'blur(10px)',
  },
  cardTitle: {
    margin: '0 0 8px',
    fontSize: '1.3rem',
    fontWeight: '600',
    color: '#ffffff',
  },
  cardDesc: {
    margin: '0 0 32px',
    fontSize: '0.9rem',
    color: 'rgba(255,255,255,0.5)',
  },
  fieldGroup: {
    marginBottom: '28px',
  },
  label: {
    display: 'block',
    marginBottom: '10px',
    fontSize: '0.85rem',
    color: 'rgba(255,255,255,0.7)',
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
    fontWeight: '600',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(212,175,55,0.25)',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '1rem',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  },
  focusGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  focusBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '14px 18px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'left',
  },
  focusBtnActive: {
    background: 'rgba(212,175,55,0.1)',
    border: '1px solid rgba(212,175,55,0.5)',
  },
  focusBtnLabel: {
    fontSize: '0.95rem',
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: '2px',
  },
  focusBtnDesc: {
    fontSize: '0.8rem',
    color: 'rgba(255,255,255,0.45)',
  },
  errorMsg: {
    color: '#ff6b6b',
    fontSize: '0.85rem',
    marginBottom: '16px',
  },
  submitBtn: {
    width: '100%',
    padding: '14px',
    background: 'linear-gradient(135deg, #d4af37, #b8942a)',
    border: 'none',
    borderRadius: '10px',
    color: '#0a0f1e',
    fontSize: '1rem',
    fontWeight: '700',
    cursor: 'pointer',
    letterSpacing: '0.03em',
    transition: 'opacity 0.2s',
  },
  footer: {
    fontSize: '0.75rem',
    color: 'rgba(255,255,255,0.2)',
    textAlign: 'center',
  },
};
